import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, TouchEvent } from "react";
import { ReaderCatalogPanel } from "@web/components/features/reader/reader-catalog-panel";
import { ReaderContent } from "@web/components/features/reader/reader-content";
import { ReaderEmptyState } from "@web/components/features/reader/reader-empty-state";
import { ReaderLoadingState } from "@web/components/features/reader/reader-loading-state";
import { ReaderNextPullHint } from "@web/components/features/reader/reader-next-pull-hint";
import { ReaderPageTurner } from "@web/components/features/reader/reader-page-turner";
import { ReaderSettingsPanel } from "@web/components/features/reader/reader-settings-panel";
import { MobileReaderToolbar, ReaderToolbar } from "@web/components/features/reader/reader-toolbar";
import type { ReaderWorkspace } from "./reader-workspace";
import { getAdjacentChapter, pickReadingParagraphIndex, pickReadingProgressChapter } from "./reader-library";
import type { ReaderHistoryGuard, ReaderPanel } from "./types";

const nextChapterPullMinDistance = 36;
const nextChapterPullBaseDistance = 132;
const nextChapterPullFastDistanceBonus = 44;
const nextChapterPullBottomOffset = 18;
const nextChapterPullVelocityForFullBonus = 1.2;
const chapterScrollRestoreReleaseDelay = 420;

interface NextChapterPullState {
  active: boolean;
  distance: number;
  progress: number;
  armed: boolean;
  firing: boolean;
}

interface NextChapterPullMetrics {
  distance: number;
  progress: number;
  armed: boolean;
}

interface PendingChapterScrollRestore {
  chapterId: string;
  top: number;
}

const idleNextChapterPullState: NextChapterPullState = {
  active: false,
  distance: 0,
  progress: 0,
  armed: false,
  firing: false,
};

function getNextChapterPullMetrics(startY: number, currentY: number, upwardVelocity: number): NextChapterPullMetrics {
  const distance = Math.max(0, startY - currentY);
  const velocityRatio = Math.min(1, upwardVelocity / nextChapterPullVelocityForFullBonus);
  const requiredDistance = nextChapterPullBaseDistance - (nextChapterPullFastDistanceBonus * velocityRatio);
  const effectiveRange = Math.max(1, requiredDistance - nextChapterPullMinDistance);
  const progress = distance < nextChapterPullMinDistance
    ? 0
    : Math.min(1, (distance - nextChapterPullMinDistance) / effectiveRange);

  return {
    distance,
    progress,
    armed: progress >= 1,
  };
}

function applyReaderScrollTop(top: number) {
  const scrollableHeight = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const boundedTop = Math.max(0, Math.min(scrollableHeight, top));

  window.scrollTo(0, boundedTop);
  document.documentElement.scrollTop = boundedTop;
  document.body.scrollTop = boundedTop;
}

function getChapterRestoreScrollTop(chapterId: string, paragraphIndex: number | undefined, scrollRatio: number, forceTop: number | null) {
  if (forceTop !== null) return forceTop;

  if (paragraphIndex !== undefined && paragraphIndex > 0) {
    const paragraph = document.querySelector<HTMLElement>(
      `[data-reader-chapter-id="${CSS.escape(chapterId)}"] [data-reader-paragraph-index="${paragraphIndex}"]`,
    );

    if (paragraph) {
      return Math.max(0, window.scrollY + paragraph.getBoundingClientRect().top);
    }
  }

  const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
  return Math.max(0, scrollableHeight * scrollRatio);
}

export function ReaderPage({ workspace }: { workspace: ReaderWorkspace }) {
  const workspaceRef = useRef(workspace);
  const toolbarScrollTopRef = useRef(0);
  const restoredChapterIdRef = useRef<string | null>(null);
  const restoringChapterScrollRef = useRef(false);
  const scrollSaveGenerationRef = useRef(0);
  const pendingChapterScrollRestoreRef = useRef<PendingChapterScrollRestore | null>(null);
  const nextPullStartYRef = useRef<number | null>(null);
  const nextPullLastYRef = useRef<number | null>(null);
  const nextPullLastTimeRef = useRef(0);
  const nextPullStartedAtBottomRef = useRef(false);
  const nextPullArmedRef = useRef(false);
  const nextPullTriggeredRef = useRef(false);
  // 统一接管临时阅读书籍的浏览器返回流程
  const historyGuardRef = useRef<ReaderHistoryGuard>({
    restoringReader: false,
    allowNextLeave: false,
    pendingTargetPath: null,
    running: false,
  });
  const preference = workspace.preference;
  const [panel, setPanel] = useState<ReaderPanel>(null);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 768px)").matches);
  const [fullscreen, setFullscreen] = useState(() => Boolean(document.fullscreenElement));
  const [immersiveFullscreen, setImmersiveFullscreen] = useState(false);
  const [fullscreenSupported, setFullscreenSupported] = useState(() => Boolean(document.fullscreenEnabled || shouldUseImmersiveFullscreenFallback()));
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const [nextChapterPull, setNextChapterPull] = useState<NextChapterPullState>(idleNextChapterPullState);
  const activeChapterId = workspace.activeChapterId ?? workspace.opened?.chapter.chapterId;
  const currentTurnerChapterId = activeChapterId ?? workspace.opened?.chapter.chapterId;
  const currentChapterIndex = useMemo(() => {
    if (!workspace.opened || !activeChapterId) return -1;
    return workspace.opened.chapters.findIndex(chapter => chapter.id === activeChapterId);
  }, [activeChapterId, workspace.opened]);
  const previousChapter = useMemo(() => {
    if (!workspace.opened || !currentTurnerChapterId) return null;
    return getAdjacentChapter(workspace.opened.chapters, currentTurnerChapterId, -1);
  }, [currentTurnerChapterId, workspace.opened]);
  const nextChapter = useMemo(() => {
    if (!workspace.opened || !currentTurnerChapterId) return null;
    return getAdjacentChapter(workspace.opened.chapters, currentTurnerChapterId, 1);
  }, [currentTurnerChapterId, workspace.opened]);
  const activeChapterTitle = currentChapterIndex > -1 ? workspace.opened?.chapters[currentChapterIndex]?.title : workspace.opened?.chapter.title;
  const readerTextColor = preference.theme === "dark" ? preference.darkTextColor : preference.lightTextColor;
  const readerBackgroundColor = preference.theme === "dark" ? preference.darkBackgroundColor : preference.lightBackgroundColor;
  const fullscreenActive = fullscreen || immersiveFullscreen;

  const getScrollRatio = useCallback(() => {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return 0;
    return Number(Math.min(1, Math.max(0, window.scrollY / scrollableHeight)).toFixed(4));
  }, []);

  const isReaderScrolledToBottom = useCallback(() => {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    return scrollableHeight <= nextChapterPullBottomOffset || window.scrollY >= scrollableHeight - nextChapterPullBottomOffset;
  }, []);

  const getVisibleChapterId = useCallback(() => {
    const chapterElements = [...document.querySelectorAll<HTMLElement>("[data-reader-chapter-id]")];
    const viewports = chapterElements.map(element => {
      const rect = element.getBoundingClientRect();
      return {
        chapterId: element.dataset.readerChapterId ?? "",
        top: rect.top,
        bottom: rect.bottom,
      };
    }).filter(chapter => chapter.chapterId.length > 0);

    return pickReadingProgressChapter(viewports, Math.min(160, window.innerHeight * 0.35));
  }, []);

  const getVisibleParagraphIndex = useCallback((chapterId: string | null) => {
    if (!chapterId) return undefined;
    const paragraphElements = [...document.querySelectorAll<HTMLElement>(`[data-reader-chapter-id="${CSS.escape(chapterId)}"] [data-reader-paragraph-index]`)];
    const viewports = paragraphElements.map(element => {
      const rect = element.getBoundingClientRect();
      return {
        index: Number(element.dataset.readerParagraphIndex ?? 0),
        top: rect.top,
        bottom: rect.bottom,
      };
    });

    return pickReadingParagraphIndex(viewports, Math.min(160, window.innerHeight * 0.35));
  }, []);

  const saveCurrentPosition = useCallback(async () => {
    const visibleChapterId = getVisibleChapterId();
    await workspaceRef.current.handleSaveCurrentProgress({
      chapterId: visibleChapterId ?? undefined,
      paragraphIndex: getVisibleParagraphIndex(visibleChapterId),
      scrollRatio: getScrollRatio(),
    });
  }, [getScrollRatio, getVisibleChapterId, getVisibleParagraphIndex]);

  const openAdjacentChapter = useCallback(
    async (offset: -1 | 1, options: { restoreScroll?: "top" } = {}) => {
      const visibleChapterId = getVisibleChapterId() ?? undefined;
      const currentWorkspace = workspaceRef.current;
      const baseChapterId = visibleChapterId ?? currentWorkspace.activeChapterId ?? currentWorkspace.opened?.chapter.chapterId;
      const restoreChapter = options.restoreScroll === "top" && currentWorkspace.opened && baseChapterId
        ? getAdjacentChapter(currentWorkspace.opened.chapters, baseChapterId, offset)
        : null;

      if (restoreChapter) {
        // 换章后短时间屏蔽滚动保存 避免 Safari 惯性滚动写回旧位置
        pendingChapterScrollRestoreRef.current = { chapterId: restoreChapter.id, top: 0 };
        restoringChapterScrollRef.current = true;
        scrollSaveGenerationRef.current += 1;
      }

      await saveCurrentPosition();
      await workspaceRef.current.handleOpenAdjacentChapter(offset, visibleChapterId);

      if (restoreChapter) {
        window.setTimeout(() => {
          if (pendingChapterScrollRestoreRef.current?.chapterId !== restoreChapter.id) return;
          if (restoredChapterIdRef.current === restoreChapter.id) return;

          pendingChapterScrollRestoreRef.current = null;
          restoringChapterScrollRef.current = false;
        }, 1200);
      }
    },
    [getVisibleChapterId, saveCurrentPosition],
  );

  const resetNextChapterPull = useCallback(() => {
    nextPullStartYRef.current = null;
    nextPullLastYRef.current = null;
    nextPullLastTimeRef.current = 0;
    nextPullStartedAtBottomRef.current = false;
    nextPullArmedRef.current = false;
    nextPullTriggeredRef.current = false;
    setNextChapterPull(idleNextChapterPullState);
  }, []);

  const triggerNextChapterPull = useCallback(() => {
    if (nextPullTriggeredRef.current) return;

    nextPullTriggeredRef.current = true;
    setNextChapterPull({
      active: true,
      distance: nextChapterPullBaseDistance,
      progress: 1,
      armed: true,
      firing: true,
    });

    if (!nextChapter) {
      window.setTimeout(resetNextChapterPull, 700);
      return;
    }

    void openAdjacentChapter(1, { restoreScroll: "top" }).finally(() => {
      window.setTimeout(resetNextChapterPull, 280);
    });
  }, [nextChapter, openAdjacentChapter, resetNextChapterPull]);

  const handleReaderTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (isDesktop || !workspaceRef.current.opened) return;
      const touch = event.touches[0];
      if (!touch) return;

      nextPullStartYRef.current = touch.clientY;
      nextPullLastYRef.current = touch.clientY;
      nextPullLastTimeRef.current = performance.now();
      nextPullStartedAtBottomRef.current = isReaderScrolledToBottom();
      nextPullArmedRef.current = false;
      nextPullTriggeredRef.current = false;
      setNextChapterPull(idleNextChapterPullState);
    },
    [isDesktop, isReaderScrolledToBottom],
  );

  const handleReaderTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (isDesktop || !workspaceRef.current.opened || nextPullTriggeredRef.current) return;
      const touch = event.touches[0];
      if (!touch) return;

      // 必须从底部开始上拉 避免正文中部的一次长滑误触换章
      if (!nextPullStartedAtBottomRef.current) {
        return;
      }

      const startY = nextPullStartYRef.current ?? touch.clientY;
      const pullDistance = Math.max(0, startY - touch.clientY);

      if (pullDistance <= 0) {
        resetNextChapterPull();
        return;
      }

      const now = performance.now();
      const lastY = nextPullLastYRef.current ?? touch.clientY;
      const lastTime = nextPullLastTimeRef.current || now;
      const elapsed = Math.max(16, now - lastTime);
      const upwardVelocity = Math.max(0, (lastY - touch.clientY) / elapsed);
      nextPullLastYRef.current = touch.clientY;
      nextPullLastTimeRef.current = now;
      const metrics = getNextChapterPullMetrics(startY, touch.clientY, upwardVelocity);

      if (event.cancelable) {
        // 底部上拉由阅读器接管 防止 iOS 橡皮筋继续推动页面
        event.preventDefault();
      }

      nextPullArmedRef.current = metrics.armed;

      setNextChapterPull(current => current.firing
        ? current
        : {
            active: true,
            distance: metrics.distance,
            progress: metrics.progress,
            armed: metrics.armed,
            firing: false,
          });
    },
    [isDesktop, resetNextChapterPull],
  );

  const handleReaderTouchEnd = useCallback((event: TouchEvent<HTMLElement>) => {
    if (nextPullTriggeredRef.current) return;
    if (!nextPullStartedAtBottomRef.current) {
      resetNextChapterPull();
      return;
    }

    const touch = event.changedTouches[0];
    const startY = nextPullStartYRef.current;
    const currentY = touch?.clientY ?? nextPullLastYRef.current;
    let shouldTrigger = nextPullArmedRef.current;

    if (startY !== null && currentY !== null) {
      const now = performance.now();
      const lastY = nextPullLastYRef.current ?? currentY;
      const lastTime = nextPullLastTimeRef.current || now;
      const elapsed = Math.max(16, now - lastTime);
      const upwardVelocity = Math.max(0, (lastY - currentY) / elapsed);
      const metrics = getNextChapterPullMetrics(startY, currentY, upwardVelocity);
      shouldTrigger = metrics.armed || (nextPullArmedRef.current && metrics.distance >= nextChapterPullMinDistance);
    }

    if (shouldTrigger) {
      triggerNextChapterPull();
      return;
    }
    resetNextChapterPull();
  }, [resetNextChapterPull, triggerNextChapterPull]);

  const handleReaderTouchCancel = useCallback(() => {
    if (!nextPullTriggeredRef.current) resetNextChapterPull();
  }, [resetNextChapterPull]);

  const openChapterFromCatalog = useCallback(
    async (chapterId: string) => {
      await saveCurrentPosition();
      await workspaceRef.current.handleOpenChapterFromDirectory(chapterId);
    },
    [saveCurrentPosition],
  );

  const refreshCurrentContent = useCallback(async () => {
    const visibleChapterId = getVisibleChapterId();
    const currentWorkspace = workspaceRef.current;
    const refreshChapterId = visibleChapterId ?? currentWorkspace.activeChapterId ?? currentWorkspace.opened?.chapter.chapterId;

    if (!refreshChapterId) return;

    const paragraphIndex = getVisibleParagraphIndex(refreshChapterId);
    const scrollRatio = getScrollRatio();

    await saveCurrentPosition();
    await workspaceRef.current.handleOpenChapterFromDirectory(refreshChapterId, {
      forceRemote: true,
      initialParagraphIndex: paragraphIndex,
      initialScrollRatio: scrollRatio,
    });
  }, [getScrollRatio, getVisibleChapterId, getVisibleParagraphIndex, saveCurrentPosition]);

  const returnToShelf = useCallback(async () => {
    await saveCurrentPosition();
    await workspaceRef.current.handleReturnToShelf();
  }, [saveCurrentPosition]);

  const scrollReaderPage = useCallback(
    async (direction: "top" | "bottom" | "up" | "down") => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pageOffset = Math.max(120, window.innerHeight - 100);
      const nextTop = {
        top: 0,
        bottom: scrollableHeight,
        up: window.scrollY - pageOffset,
        down: window.scrollY + pageOffset,
      }[direction];
      const boundedTop = Math.max(0, Math.min(scrollableHeight, nextTop));

      window.scrollTo({ top: boundedTop, behavior: "auto" });
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      const visibleChapterId = getVisibleChapterId();
      await workspaceRef.current.handleSaveCurrentProgress({
        chapterId: visibleChapterId ?? undefined,
        paragraphIndex: getVisibleParagraphIndex(visibleChapterId),
        scrollRatio: getScrollRatio(),
      });
    },
    [getScrollRatio, getVisibleChapterId, getVisibleParagraphIndex],
  );

  const toggleFullscreen = useCallback(() => {
    if (immersiveFullscreen) {
      setImmersiveFullscreen(false);
      return;
    }

    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => setFullscreen(Boolean(document.fullscreenElement)));
      return;
    }

    if (shouldUseImmersiveFullscreenFallback()) {
      // iPad Safari 的下滑退出是系统手势 这里改用阅读器内沉浸态
      setImmersiveFullscreen(true);
      return;
    }

    if (!document.fullscreenEnabled) return;

    // 浏览器要求全屏请求必须来自用户手势
    void document.documentElement.requestFullscreen({ navigationUI: "hide" }).catch(() => setFullscreen(Boolean(document.fullscreenElement)));
  }, [immersiveFullscreen]);

  workspaceRef.current = workspace;

  useEffect(() => {
    if (isDesktop || !workspace.opened) resetNextChapterPull();
  }, [isDesktop, resetNextChapterPull, workspace.opened]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const syncViewport = () => setIsDesktop(mediaQuery.matches);

    syncViewport();
    mediaQuery.addEventListener("change", syncViewport);
    return () => mediaQuery.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    const syncFullscreen = () => {
      setFullscreen(Boolean(document.fullscreenElement));
      setFullscreenSupported(Boolean(document.fullscreenEnabled || document.fullscreenElement || shouldUseImmersiveFullscreenFallback()));
    };

    syncFullscreen();
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () => document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  useEffect(() => {
    if (fullscreen) setImmersiveFullscreen(false);
  }, [fullscreen]);

  useEffect(() => {
    document.documentElement.classList.toggle("reader-fullscreen-lock", fullscreenActive);
    return () => document.documentElement.classList.remove("reader-fullscreen-lock");
  }, [fullscreenActive]);

  useEffect(() => {
    if (!workspace.opened) return;
    document.title = `${workspace.selectedBookTitle} | ${activeChapterTitle ?? workspace.opened.chapter.title}`;
  }, [activeChapterTitle, workspace.opened, workspace.selectedBookTitle]);

  useLayoutEffect(() => {
    const chapterId = activeChapterId ?? workspace.opened?.chapter.chapterId;
    if (!workspace.opened || !chapterId) {
      restoredChapterIdRef.current = null;
      pendingChapterScrollRestoreRef.current = null;
      restoringChapterScrollRef.current = false;
      return;
    }
    if (restoredChapterIdRef.current === chapterId) return;

    restoredChapterIdRef.current = chapterId;
    scrollSaveGenerationRef.current += 1;
    restoringChapterScrollRef.current = true;

    if (pendingChapterScrollRestoreRef.current && pendingChapterScrollRestoreRef.current.chapterId !== chapterId) {
      pendingChapterScrollRestoreRef.current = null;
    }

    const pendingRestore = pendingChapterScrollRestoreRef.current?.chapterId === chapterId
      ? pendingChapterScrollRestoreRef.current
      : null;
    const restoreTop = () => {
      applyReaderScrollTop(getChapterRestoreScrollTop(
        chapterId,
        workspace.restoreParagraphIndex,
        workspace.restoreScrollRatio,
        pendingRestore?.top ?? null,
      ));
    };
    let firstFrame = 0;
    let secondFrame = 0;
    let releaseFrame = 0;

    // 章节首次渲染后恢复到目标位置 iOS Safari 需要多帧兜底
    restoreTop();
    firstFrame = requestAnimationFrame(() => {
      restoreTop();
      secondFrame = requestAnimationFrame(restoreTop);
    });

    const releaseTimeout = window.setTimeout(() => {
      restoreTop();
      releaseFrame = requestAnimationFrame(() => {
        restoreTop();
        if (pendingChapterScrollRestoreRef.current?.chapterId === chapterId) {
          pendingChapterScrollRestoreRef.current = null;
        }
        restoringChapterScrollRef.current = false;
      });
    }, chapterScrollRestoreReleaseDelay);

    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      cancelAnimationFrame(releaseFrame);
      window.clearTimeout(releaseTimeout);
      if (pendingChapterScrollRestoreRef.current?.chapterId === chapterId) {
        pendingChapterScrollRestoreRef.current = null;
      }
      if (restoredChapterIdRef.current === chapterId) {
        restoredChapterIdRef.current = null;
      }
      restoringChapterScrollRef.current = false;
    };
  }, [activeChapterId, workspace.opened?.chapter.chapterId]);

  useEffect(() => {
    // 和 legado 一样把阅读页快捷键绑定到页面生命周期
    const handleKeyup = (event: KeyboardEvent) => {
      if (!workspace.opened) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        void openAdjacentChapter(-1, { restoreScroll: "top" });
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        void openAdjacentChapter(1, { restoreScroll: "top" });
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        void scrollReaderPage("up");
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        void scrollReaderPage("down");
      }
    };
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("keyup", handleKeyup);
    window.addEventListener("keydown", handleKeydown);
    return () => {
      window.removeEventListener("keyup", handleKeyup);
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [openAdjacentChapter, scrollReaderPage, workspace.opened]);

  useEffect(() => {
    if (!workspace.opened) {
      historyGuardRef.current.restoringReader = false;
      historyGuardRef.current.allowNextLeave = false;
      historyGuardRef.current.pendingTargetPath = null;
      historyGuardRef.current.running = false;
      return;
    }

    const handlePopstate = () => {
      const guard = historyGuardRef.current;
      const targetPath = window.location.pathname;

      if (guard.allowNextLeave) {
        guard.allowNextLeave = false;
        guard.pendingTargetPath = null;
        return;
      }

      if (guard.restoringReader && targetPath === "/reader") {
        guard.restoringReader = false;
        return;
      }

      if (targetPath === "/reader" || guard.running) return;

      // 浏览器返回先拦截在阅读页内 等保存进度和临时书籍确认完成再跳转
      guard.running = true;
      guard.restoringReader = true;
      guard.pendingTargetPath = targetPath;
      window.history.go(1);

      void (async () => {
        await saveCurrentPosition();
        const handled = await workspaceRef.current.handleLeaveReader();
        const nextTargetPath = guard.pendingTargetPath ?? "/shelf";
        const shouldReportShelfReturn = !handled && nextTargetPath === "/shelf";
        guard.pendingTargetPath = null;
        guard.running = false;
        guard.allowNextLeave = true;

        if (window.location.pathname === "/reader") {
          // 用 replaceState 落到目标路由 避免历史记录残留阅读页
          window.history.replaceState({}, "", nextTargetPath);
          window.dispatchEvent(new PopStateEvent("popstate"));

          if (shouldReportShelfReturn) {
            requestAnimationFrame(() => {
              workspaceRef.current.handleReaderReturned();
            });
          }
        }
      })();
    };

    window.addEventListener("popstate", handlePopstate);
    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [saveCurrentPosition, workspace.opened]);

  useEffect(() => {
    const saveWhenHidden = () => {
      if (document.visibilityState === "hidden") void saveCurrentPosition();
    };

    document.addEventListener("visibilitychange", saveWhenHidden);
    return () => {
      document.removeEventListener("visibilitychange", saveWhenHidden);
      void saveCurrentPosition();
    };
  }, [saveCurrentPosition]);

  useEffect(() => {
    return () => {
      // 只在真实离开阅读路由时处理临时书籍确认
      if (window.location.pathname !== "/reader") {
        void workspaceRef.current.handleLeaveReader();
      }
    };
  }, []);

  useEffect(() => {
    if (!workspace.opened) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const saveAfterScrollStops = () => {
      if (restoringChapterScrollRef.current) return;
      const visibleChapterId = getVisibleChapterId();
      if (visibleChapterId) workspaceRef.current.handleActiveChapterChange(visibleChapterId);

      // 滚动中频繁触发只更新当前章节 真正保存等用户停下后再做
      const saveGeneration = scrollSaveGenerationRef.current;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (saveGeneration !== scrollSaveGenerationRef.current || restoringChapterScrollRef.current) return;
        void saveCurrentPosition();
      }, 1200);
    };

    window.addEventListener("scroll", saveAfterScrollStops, { passive: true });
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("scroll", saveAfterScrollStops);
    };
  }, [getVisibleChapterId, saveCurrentPosition, workspace.opened]);

  useEffect(() => {
    if (!workspace.opened) {
      setToolbarVisible(true);
      return;
    }

    toolbarScrollTopRef.current = window.scrollY;

    const syncToolbarVisibility = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const nextScrollTop = Math.max(0, window.scrollY);
      const scrollDelta = nextScrollTop - toolbarScrollTopRef.current;

      // 没有可滚动内容时顶部栏保持展示
      if (scrollableHeight <= 4) {
        toolbarScrollTopRef.current = nextScrollTop;
        setToolbarVisible(true);
        return;
      }

      if (Math.abs(scrollDelta) < 6) return;

      setToolbarVisible(nextScrollTop < 20 || scrollDelta < 0);
      toolbarScrollTopRef.current = nextScrollTop;
    };

    syncToolbarVisibility();
    window.addEventListener("scroll", syncToolbarVisibility, { passive: true });
    window.addEventListener("resize", syncToolbarVisibility);
    return () => {
      window.removeEventListener("scroll", syncToolbarVisibility);
      window.removeEventListener("resize", syncToolbarVisibility);
    };
  }, [workspace.opened]);

  if (!workspace.opened) {
    if (workspace.readerOpeningTitle) {
      return (
        <ReaderLoadingState
          title={workspace.readerOpeningTitle}
          message={workspace.message}
          onReturn={() => void returnToShelf()}
        />
      );
    }

    return <ReaderEmptyState onReturn={() => void returnToShelf()} />;
  }

  return (
    <section className={preference.theme === "dark" ? "dark" : ""}>
      <div
        className={`reader-page-surface relative min-h-screen bg-background bg-cover bg-fixed bg-center text-foreground ${fullscreenActive ? "reader-fullscreen-active" : ""}`}
        onTouchStart={handleReaderTouchStart}
        onTouchMove={handleReaderTouchMove}
        onTouchEnd={handleReaderTouchEnd}
        onTouchCancel={handleReaderTouchCancel}
        style={
          {
            color: readerTextColor || undefined,
            backgroundColor: readerBackgroundColor || undefined,
            backgroundImage: preference.backgroundImageUrl ? `url("${preference.backgroundImageUrl}")` : undefined,
          } as CSSProperties
        }
      >
        {isDesktop ? (
          <ReaderToolbar
            activePanel={panel}
            theme={preference.theme}
            title={activeChapterTitle ?? workspace.opened.chapter.title}
            visible={panel !== null || toolbarVisible}
            onPanelChange={setPanel}
            onThemeToggle={workspace.handleToggleTheme}
            onReturn={() => void returnToShelf()}
          />
        ) : (
          <MobileReaderToolbar
            activePanel={panel}
            theme={preference.theme}
            title={activeChapterTitle ?? workspace.opened.chapter.title}
            visible={panel !== null || toolbarVisible}
            onPanelChange={setPanel}
            onThemeToggle={workspace.handleToggleTheme}
            onReturn={() => void returnToShelf()}
          />
        )}

        <div className="reader-safe-content min-h-screen w-full pt-14">
          <ReaderContent workspace={workspace} activeChapterId={activeChapterId} />
          <ReaderPageTurner
            hasPrevious={previousChapter !== null}
            hasNext={nextChapter !== null}
            onPrevious={() => void openAdjacentChapter(-1, { restoreScroll: "top" })}
            onNext={() => void openAdjacentChapter(1, { restoreScroll: "top" })}
          />
        </div>
        <ReaderNextPullHint
          active={!isDesktop && nextChapterPull.active}
          progress={nextChapterPull.progress}
          armed={nextChapterPull.armed}
          firing={nextChapterPull.firing}
          hasNext={nextChapter !== null}
        />
      </div>

      {panel === "catalog" && (
        <ReaderCatalogPanel
          workspace={workspace}
          activeChapterId={activeChapterId}
          onClose={() => setPanel(null)}
          onContentRefresh={refreshCurrentContent}
          onChapterSelect={openChapterFromCatalog}
        />
      )}
      {panel === "settings" && (
        <ReaderSettingsPanel
          workspace={workspace}
          fullscreen={fullscreenActive}
          fullscreenSupported={fullscreenSupported}
          onClose={() => setPanel(null)}
          onFullscreenToggle={toggleFullscreen}
        />
      )}
    </section>
  );
}

function shouldUseImmersiveFullscreenFallback(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform.toLowerCase();
  const isAppleTablet = /ipad/.test(userAgent) || (platform === "macintel" && navigator.maxTouchPoints > 1);
  const isSafari = /safari/.test(userAgent) && !/crios|fxios|edgios/.test(userAgent);

  return isAppleTablet && isSafari;
}
